import { useEffect } from 'react';
import { observer } from 'mobx-react';

import { applyConsentStyleOverrides, useHideStyle } from './PrivacyBanner.helpers';
import { useStore, usePrivacyConfig } from '../hooks';

const INJECTED_ATTR = 'data-privacy-banner';

// Banner script executed on this page. A consent script (reddot.js) can't be unloaded once it ran
// (it renders into <body>, patches document.cookie and starts timers), so each page load runs at most one.
let loadedScriptBanner: string | undefined;

// script_banner may be a plain script URL or an HTML snippet (<script>/<link>/<style> tags)
function createBannerElements(scriptBanner: string): Element[] {
    if (!scriptBanner.startsWith('<')) {
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = scriptBanner;
        return [script];
    }

    const template = document.createElement('template');
    template.innerHTML = scriptBanner;

    return Array.from(template.content.children).map(node => {
        if (node.tagName !== 'SCRIPT') {
            return document.importNode(node, true);
        }

        // Scripts parsed via innerHTML never execute, so re-create them
        const script = document.createElement('script');
        Array.from(node.attributes).forEach(attr =>
            script.setAttribute(attr.name, attr.value)
        );
        script.text = node.textContent ?? '';
        return script;
    });
}

function PrivacyBanner() {
    const { subDashboardStore } = useStore();
    const scriptBanner = usePrivacyConfig()?.script_banner?.trim() || undefined;
    const setHidden = useHideStyle();

    useEffect(() => {
        // Selected menu has no banner: hide the one already rendered (if any)
        if (!scriptBanner) {
            setHidden(true);
            return;
        }

        if (!loadedScriptBanner) {
            applyConsentStyleOverrides();
            createBannerElements(scriptBanner).forEach(element => {
                element.setAttribute(INJECTED_ATTR, '');
                document.head.appendChild(element);
            });
            loadedScriptBanner = scriptBanner;
        }

        if (loadedScriptBanner === scriptBanner) {
            setHidden(false);
            return;
        }

        // Another company uses a different banner script: reload to start clean, keeping the selected menu
        subDashboardStore.persistSelectionForReload();
        window.location.reload();
    }, [scriptBanner, subDashboardStore, setHidden]);

    // Hide the banner when leaving the dashboard layout (e.g. logout)
    useEffect(() => () => setHidden(true), [setHidden]);

    // The banner itself is rendered by the injected script (e.g. reddot.js)
    return null;
}

export default observer(PrivacyBanner);
